import React, {useContext, useEffect, useState} from "react";
import {Form} from "react-bootstrap";
import {format} from "date-fns";
import CustomDatePicker from "common/components/DatePicker";
import ModalDialog from "common/components/Modal";
import {AlertContext} from "context/AlertContext";
import {EVENT_STATUS_OPTIONS} from "./constants";

const DT_FORMAT = "yyyy-MM-dd HH:mm:ss";

function parseDate(value) {
  if (!value) return null;
  return new Date(value.replace(" ", "T"));
}

export default function EventSettings({
  api,
  events,
  selectedEventId,
  event,
  onEventChanged,
  refreshEvents,
}) {
  const {setShow, setToastMsg} = useContext(AlertContext);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [startAt, setStartAt] = useState(null);
  const [endAt, setEndAt] = useState(null);
  const [initialTickets, setInitialTickets] = useState(2);
  const [pointsPerTicket, setPointsPerTicket] = useState(50);
  const [maxTicketsDaily, setMaxTicketsDaily] = useState(3);
  const [maxTicketsMatch, setMaxTicketsMatch] = useState(3);
  const [bettingCloseHours, setBettingCloseHours] = useState(2);

  useEffect(() => {
    if (!event || isCreating) return;
    setTitle(event.title ?? "");
    setStatus(event.status ?? "DRAFT");
    setStartAt(parseDate(event.startAt));
    setEndAt(parseDate(event.endAt));
    setInitialTickets(event.initialTickets ?? 2);
    setPointsPerTicket(event.pointsPerTicket ?? 50);
    setMaxTicketsDaily(event.maxTicketsDaily ?? 3);
    setMaxTicketsMatch(event.maxTicketsMatch ?? 3);
    setBettingCloseHours(event.bettingCloseHours ?? 2);
  }, [event, isCreating]);

  function resetCreateForm() {
    setTitle("야옹상회 월드컵 예측 이벤트");
    setStatus("DRAFT");
    setStartAt(new Date());
    setEndAt(new Date());
    setInitialTickets(2);
    setPointsPerTicket(50);
    setMaxTicketsDaily(3);
    setMaxTicketsMatch(3);
    setBettingCloseHours(2);
  }

  function validate() {
    if (!title?.trim() || !startAt || !endAt) {
      alert("제목과 기간을 입력해주세요.");
      return false;
    }
    return true;
  }

  function buildPayload() {
    return {
      title: title.trim(),
      status,
      startAt: format(startAt, DT_FORMAT),
      endAt: format(endAt, DT_FORMAT),
      initialTickets: Number(initialTickets),
      pointsPerTicket: Number(pointsPerTicket),
      maxTicketsDaily: Number(maxTicketsDaily),
      maxTicketsMatch: Number(maxTicketsMatch),
      bettingCloseHours: Number(bettingCloseHours),
    };
  }

  function onSave() {
    if (!validate()) {
      setConfirmOpen(false);
      return;
    }
    const payload = buildPayload();
    const request = isCreating
      ? api.createWorldcupEvent(payload)
      : api.updateWorldcupEvent({...payload, eventId: selectedEventId});

    request.then((res) => {
      if (res?.result === "ok") {
        setToastMsg(isCreating ? "이벤트가 생성되었습니다." : "저장되었습니다.");
        setIsCreating(false);
        refreshEvents().then((list) => {
          if (isCreating && res.eventId) {
            onEventChanged(res.eventId);
          } else {
            onEventChanged(selectedEventId);
          }
        });
      } else {
        setToastMsg(res?.message ?? "실패하였습니다.");
      }
      setShow(true);
    }).finally(() => setConfirmOpen(false));
  }

  return (
    <article>
      {(isCreating || selectedEventId) ? (
        <div className="form-container event-form worldcup-form">
          <div><span>* 이벤트 제목</span></div>
          <div>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={{width: "100%"}}/>
          </div>

          <div><span>* 상태</span></div>
          <div className="type-input">
            {EVENT_STATUS_OPTIONS.map((opt) => (
              <Form.Check
                key={opt.value}
                type="radio"
                id={`wc-status-${opt.value}`}
                name="wc-status"
                label={opt.label}
                checked={status === opt.value}
                onChange={() => setStatus(opt.value)}
              />
            ))}
          </div>

          <div><span>* 시작일시</span></div>
          <div>
            <CustomDatePicker placeholder="시작일시" value={startAt} showTime onChange={setStartAt}/>
          </div>

          <div><span>* 종료일시</span></div>
          <div>
            <CustomDatePicker placeholder="종료일시" value={endAt} showTime onChange={setEndAt}/>
          </div>

          <div><span>최초 지급 티켓</span></div>
          <div>
            <input type="number" min={0} value={initialTickets}
                   onChange={(e) => setInitialTickets(e.target.value)}/>
          </div>

          <div><span>티켓 1장당 풀 적립P</span></div>
          <div>
            <input type="number" min={1} value={pointsPerTicket}
                   onChange={(e) => setPointsPerTicket(e.target.value)}/>
          </div>

          <div><span>일일 최대 티켓</span></div>
          <div>
            <input type="number" min={1} value={maxTicketsDaily}
                   onChange={(e) => setMaxTicketsDaily(e.target.value)}/>
          </div>

          <div><span>경기당 최대 티켓</span></div>
          <div>
            <input type="number" min={1} value={maxTicketsMatch}
                   onChange={(e) => setMaxTicketsMatch(e.target.value)}/>
          </div>

          <div><span>예측 마감(시간)</span></div>
          <div>
            <input type="number" min={1} value={bettingCloseHours}
                   onChange={(e) => setBettingCloseHours(e.target.value)}/>
            <span style={{marginLeft: 8}}>경기 시작 N시간 전</span>
          </div>
        </div>
      ) : (
        <p>이벤트를 선택하거나 새로 생성해주세요.</p>
      )}

      <div className="flex" style={{marginTop: 16, gap: 8, alignItems: "center"}}>
        {(isCreating || selectedEventId) && (
          <>
            <button type="button" className="green-btn" onClick={() => setConfirmOpen(true)}>
              {isCreating ? "생성" : "저장"}
            </button>
            {isCreating && selectedEventId && (
              <button type="button" className="grey-btn" onClick={() => setIsCreating(false)}>
                생성 취소
              </button>
            )}
          </>
        )}
        <button
          type="button"
          className="blue-btn ml-auto"
          onClick={() => {
            setIsCreating(true);
            resetCreateForm();
          }}
        >
          새 이벤트 생성
        </button>
      </div>

      {!isCreating && events?.length > 0 && (
        <TableSummary events={events}/>
      )}

      <ModalDialog
        show={confirmOpen}
        onHide={() => setConfirmOpen(false)}
        btnText="예"
        secondBtnText="아니요"
        hasTwoButtons
        onOk={onSave}
        onClickSecond={() => setConfirmOpen(false)}
      >
        <p style={{textAlign: "center"}}>
          {isCreating ? "이벤트를 생성하시겠습니까?" : "변경사항을 저장하시겠습니까?"}
        </p>
      </ModalDialog>
    </article>
  );
}

function TableSummary({events}) {
  return (
    <table className="basic-table" style={{marginTop: 32, width: "100%"}}>
      <thead>
      <tr>
        <th>ID</th>
        <th>제목</th>
        <th>상태</th>
        <th>기간</th>
      </tr>
      </thead>
      <tbody>
      {events.map((item) => (
        <tr key={item.eventId}>
          <td>{item.eventId}</td>
          <td>{item.title}</td>
          <td>{item.status}</td>
          <td>{item.startAt} ~ {item.endAt}</td>
        </tr>
      ))}
      </tbody>
    </table>
  );
}
